import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentService } from '../services/assessmentService';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const ratingValues = [1, 2, 3, 4, 5, 6];

function RatingButton({ value, isSelected, onClick }) {
  const baseClass = 'w-12 h-12 rounded-full border-2 font-bold text-sm transition-all';
  const selectedClass = 'border-brand-600 bg-brand-600 text-white scale-110 shadow-lg';
  const unselectedClass = 'border-gray-300 bg-white text-gray-700 hover:border-brand-400 hover:scale-105';
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={baseClass + ' ' + (isSelected ? selectedClass : unselectedClass)}
      data-testid={`option-${value}`}
    >
      {value}
    </button>
  );
}

function RatingGroup({ questionId, selectedValue, onSelect, t }) {
  return (
    <div className="flex flex-row items-start" style={{ gap: '20px' }}>
      {ratingValues.map(function(value) {
        return (
          <div key={value} className="flex flex-col items-center">
            <RatingButton
              value={value}
              isSelected={selectedValue === value}
              onClick={function() { onSelect(questionId, value); }}
            />
            {value === 1 ? (
              <span className="text-xs text-gray-500 mt-2 text-center">
                {t('stronglyDisagree').split(' ').map((word, i) => <span key={i}>{word}<br/></span>)}
              </span>
            ) : null}
            {value === 6 ? (
              <span className="text-xs text-gray-500 mt-2 text-center">
                {t('stronglyAgree').split(' ').map((word, i) => <span key={i}>{word}<br/></span>)}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function QuestionCard({ question, questionNumber, totalQuestions, response, onResponseChange, t }) {
  const hasResponse = response !== undefined;
  const borderClass = hasResponse ? 'border-brand-200' : 'border-transparent';
  
  return (
    <div className={'bg-white rounded-xl shadow-md p-6 border-2 transition-all ' + borderClass}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-1">
          {hasResponse ? (
            <CheckCircle className="w-5 h-5 text-brand-600" />
          ) : (
            <span className="text-sm font-bold text-gray-600">{questionNumber}</span>
          )}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 leading-relaxed mb-4">
            {question.question_text}
          </h3>
          <RatingGroup
            questionId={question.id}
            selectedValue={response}
            onSelect={onResponseChange}
            t={t}
          />
        </div>
      </div>
    </div>
  );
}

function AssessmentPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [allQuestions, setAllQuestions] = useState([]);

  const loadAssessment = useCallback(function() {
    setLoading(true);
    const slug = language === 'fr' ? 'wellbeing-fr' : 'wellbeing-en';
    
    assessmentService.getAssessment(slug)
      .then(function(data) {
        setAssessment(data);
        const questions = [];
        for (let i = 0; i < data.sections.length; i++) {
          for (let j = 0; j < data.sections[i].questions.length; j++) {
            questions.push(data.sections[i].questions[j]);
          }
        }
        setAllQuestions(questions);
        setLoading(false);
      })
      .catch(function(error) {
        if (language === 'fr') {
          assessmentService.getAssessment('wellbeing-en')
            .then(function(data) {
              setAssessment(data);
              const questions = [];
              for (let i = 0; i < data.sections.length; i++) {
                for (let j = 0; j < data.sections[i].questions.length; j++) {
                  questions.push(data.sections[i].questions[j]);
                }
              }
              setAllQuestions(questions);
              setLoading(false);
            })
            .catch(function(err) {
              toast.error(t('assessmentNotFound'));
              console.error(err);
              setLoading(false);
            });
        } else {
          toast.error(t('assessmentNotFound'));
          console.error(error);
          setLoading(false);
        }
      });
  }, [language, t]);

  useEffect(function() {
    loadAssessment();
  }, [loadAssessment]);

  function handleResponseChange(questionId, value) {
    setResponses(function(prev) {
      const newResponses = Object.assign({}, prev);
      newResponses[questionId] = parseInt(value, 10);
      return newResponses;
    });
  }

  function handleNext() {
    const currentQuestion = allQuestions[currentQuestionIndex];
    
    if (!responses[currentQuestion.id]) {
      toast.error(t('answerRequired'));
      return;
    }

    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/assessment/register', { state: { responses: responses, language: language } });
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loadingAssessment')}</p>
        </div>
      </div>
    );
  }

  if (!assessment || allQuestions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">{t('assessmentNotFound')}</p>
          <Button onClick={function() { navigate('/'); }} className="mt-4">{t('returnHome')}</Button>
        </div>
      </div>
    );
  }

  const currentQuestion = allQuestions[currentQuestionIndex];
  const totalQuestions = allQuestions.length;
  const answeredQuestions = Object.keys(responses).length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-brand-50/20 to-white">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h1 className="text-xl font-black text-gray-900">{t('appTitle')}</h1>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">{t('progress')}</div>
              <div className="text-lg font-bold text-brand-600">{answeredQuestions}/{totalQuestions}</div>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <QuestionCard
          question={currentQuestion}
          questionNumber={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
          response={responses[currentQuestion.id]}
          onResponseChange={handleResponseChange}
          t={t}
        />

        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleNext}
            size="lg"
            className="bg-brand-600 hover:bg-brand-700"
            data-testid="next-question-btn"
          >
            {isLastQuestion ? t('complete') : t('nextQuestion')}
            {isLastQuestion 
              ? <CheckCircle className="ml-2 h-5 w-5" />
              : <ArrowRight className="ml-2 h-5 w-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AssessmentPage;
